import { Controller } from "@hotwired/stimulus"

// Save/Cancel orchestrator for the side panel. Wraps the panel content
// frame and listens for panel:dirty events from deferred autosave-field
// and assignment-picker controllers inside.
//
// When any field becomes dirty, Save and Cancel become available.
// Save  -> dispatch panel:save-all (each deferred field flushes its own
//          PATCH), then reload after a short delay so the page reflects
//          server-side ripple effects (assignment changes, status
//          recomputes, archive button visibility, etc).
// Cancel -> dispatch panel:cancel-all (each field reverts) and clear
//          dirty state.
export default class extends Controller {
  static targets = ["saveButton", "cancelButton"]

  connect() {
    this.dirtyCount = 0
    this.boundDirty = (e) => this.onDirty(e)
    document.addEventListener("panel:dirty", this.boundDirty)
    this.updateButtons()
  }

  disconnect() {
    document.removeEventListener("panel:dirty", this.boundDirty)
  }

  onDirty(event) {
    // Each field dispatches { dirty: true } or { dirty: false }; we
    // tally a coarse counter rather than tracking per-field state.
    // A simple "any dirty" boolean would also work, but counting lets
    // us distinguish "all reverted to original" from "still dirty".
    if (event.detail?.dirty) {
      this.dirtyCount += 1
    } else {
      this.dirtyCount = Math.max(0, this.dirtyCount - 1)
    }
    this.updateButtons()
  }

  updateButtons() {
    const hasDirty = this.dirtyCount > 0
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = !hasDirty
      this.saveButtonTarget.classList.toggle("opacity-40", !hasDirty)
      this.saveButtonTarget.classList.toggle("cursor-not-allowed", !hasDirty)
    }
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.disabled = !hasDirty
      this.cancelButtonTarget.classList.toggle("opacity-40", !hasDirty)
      this.cancelButtonTarget.classList.toggle("cursor-not-allowed", !hasDirty)
    }
  }

  async save(event) {
    event?.preventDefault?.()
    if (this.dirtyCount === 0) return
    document.dispatchEvent(new CustomEvent("panel:save-all"))
    // Flushes are async; give them a beat then reload so server-side
    // ripple effects (completion records, status recomputes, archive
    // visibility) show up. The "Saved ✓" indicator briefly flashes
    // before the reload, which is fine.
    setTimeout(() => window.location.reload(), 600)
  }

  cancel(event) {
    event?.preventDefault?.()
    if (this.dirtyCount === 0) return
    document.dispatchEvent(new CustomEvent("panel:cancel-all"))
    this.dirtyCount = 0
    this.updateButtons()
  }
}
